import { HttpClient } from '@angular/common/http';
import { computed, Injectable, signal } from '@angular/core';
import { OccEndpointsService } from '@spartacus/core';
import { LOGIN_ROOT_PATH, LOGIN_TYPES } from '@user/const';
import { BehaviorSubject, catchError, defer, finalize, map, Observable, of, tap, timeout } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class LoginTypeService {
  private readonly validTypes = new Set(Object.values(LOGIN_TYPES));
  private readonly loginTypeEndpoint = this.occEndpointsService.buildUrl('/auth/preferred-method');
  private readonly loginTypeLoading$ = new BehaviorSubject(false);
  private pendingLoginTypeRequests = 0;

  loginBy = signal<LOGIN_TYPES>(LOGIN_TYPES.CALL);

  readonly isCode = computed(() => this.loginBy() === LOGIN_TYPES.CODE);
  readonly isCall = computed(() => this.loginBy() === LOGIN_TYPES.CALL);
  readonly isPassword = computed(() => this.loginBy() === LOGIN_TYPES.PASSWORD);

  private readonly LOGIN_TYPE_TIMEOUT_SECONDS = 3;

  constructor(
    private occEndpointsService: OccEndpointsService,
    private http: HttpClient,
  ) {}

  // эту всю грязь надо бы в data-access или чё мы там придумали
  // но пока реализация не финальная - оставил здесь, чтобы не плодить йибучий бойлерплейт
  private getLoginTypeForPhoneNumber(phoneNumber: string): Observable<LOGIN_TYPES> {
    const finalUrl = this.loginTypeEndpoint + `?phoneNumber=${encodeURIComponent(phoneNumber)}`;

    return this.http.get<{ method: 'call' | 'sms' }>(finalUrl).pipe(
      timeout(this.LOGIN_TYPE_TIMEOUT_SECONDS * 1000),
      map((response) => {
        if (response.method === 'sms') {
          return LOGIN_TYPES.CODE;
        } else if (response.method === 'call') {
          return LOGIN_TYPES.CALL;
        } else {
          throw new Error(`Unsupported login method: ${response.method}`);
        }
      }),
    );
  }

  getLoginTypeLoading(): Observable<boolean> {
    return this.loginTypeLoading$.asObservable();
  }

  // наивно ожидаем, что в метод попадёт правильный номер
  changeTypeByPhoneNumber(phoneNumber: string): Observable<LOGIN_TYPES> {
    return defer(() => {
      this.pendingLoginTypeRequests++;
      this.loginTypeLoading$.next(true);

      return this.getLoginTypeForPhoneNumber(phoneNumber).pipe(
        tap((type) => this.loginBy.set(type)),
        catchError(() => {
          console.warn('Failed to determine login type by phone number, defaulting to CODE');
          this.loginBy.set(LOGIN_TYPES.CODE);

          return of(LOGIN_TYPES.CODE);
        }),
        finalize(() => {
          this.pendingLoginTypeRequests = Math.max(0, this.pendingLoginTypeRequests - 1);
          this.loginTypeLoading$.next(this.pendingLoginTypeRequests > 0);
        }),
      );
    });
  }

  changeTypeBySubpath(subpath: string): void {
    const type = this.validTypes.has(subpath as LOGIN_TYPES) ? (subpath as LOGIN_TYPES) : null;
    if (type) {
      this.loginBy.set(type);
    } else {
      this.loginBy.set(LOGIN_TYPES.CALL);
      console.warn(`Unknown login type for subpath: ${subpath}, defaulting to 'call'`);
    }
  }

  getDeliveryChannelByType(): string {
    if (this.isCode()) {
      return 'sms';
    } else if (this.isCall()) {
      return 'call';
    } else {
      return '';
    }
  }

  getLoginLinkByType(type: LOGIN_TYPES): string {
    const subpath = this.validTypes.has(type) ? type : '';

    return `/${LOGIN_ROOT_PATH}` + (subpath ? `/${subpath}` : '');
  }

  getLinkCode(): string {
    return this.getLoginLinkByType(LOGIN_TYPES.CODE);
  }

  getLinkCall(): string {
    return this.getLoginLinkByType(LOGIN_TYPES.CALL);
  }

  getLinkPassword(): string {
    return this.getLoginLinkByType(LOGIN_TYPES.PASSWORD);
  }
}
